/**
 * CustomerLoginPage - Authentification Client E-commerce
 *
 * Page de connexion/inscription pour les clients e-commerce
 * Séparée du Login staff (back-office)
 *
 * Important: Le client choisit sa boutique locale (tenant)
 * - Les produits viennent de cette boutique
 * - Les livraisons se font depuis cette boutique
 * - Le client peut changer de boutique dans ses paramètres
 */
import { useState, useEffect } from 'react';
import { useTitrePage } from '../../hooks/useTitrePage';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useCustomer } from '../../contexts/CustomerContext';
import { api } from '../../services/api';
import { ecommerceApi } from '../../api/ecommerce.api';

type FormMode = 'login' | 'register';

interface TenantOption {
  id: string;
  slug: string;
  name: string;
  city: string;
}

export default function CustomerLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, login, register, selectedTenantId } = useCustomer();

  // Lire ?mode=register dans l'URL pour afficher directement le formulaire d'inscription
  const initialMode: FormMode = new URLSearchParams(location.search).get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState<FormMode>(initialMode);
  useTitrePage(mode === 'register' ? 'Créer un compte' : 'Connexion');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    tenantId: selectedTenantId || '',
  });
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Charger les tenants disponibles
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const data = await api.get('/tenants');
        setTenants(data);
        // Si pas de tenant sélectionné, prendre le premier
        if (!formData.tenantId && data.length > 0) {
          setFormData((prev) => ({ ...prev, tenantId: data[0].id }));
        }
      } catch (err) {
        console.error('Erreur chargement tenants:', err);
        setError('Impossible de charger les boutiques disponibles');
      } finally {
        setTenantsLoading(false);
      }
    };

    loadTenants();
  }, []);

  // Rediriger si déjà authentifié
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = (location.state as any)?.from?.pathname || '/shop';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!formData.tenantId) {
      setError('Veuillez sélectionner votre boutique locale');
      return;
    }

    if (mode === 'register' && (!formData.firstName || !formData.lastName)) {
      setError('Veuillez remplir votre nom et prénom');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password, formData.tenantId);
      } else {
        await register(
          formData.email,
          formData.password,
          formData.firstName,
          formData.lastName,
          formData.tenantId,
          formData.phone || undefined,
          marketingConsent
        );
      }
      // Redirection gérée par useEffect
    } catch (err: any) {
      console.error('[CustomerLogin] Erreur:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        (mode === 'login' ? 'Identifiants incorrects' : 'Erreur lors de l\'inscription')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const switchMode = () => {
    setMode((prev) => (prev === 'login' ? 'register' : 'login'));
    setError(null);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      setError('Veuillez entrer votre adresse email');
      return;
    }
    setForgotLoading(true);
    setError(null);
    try {
      await ecommerceApi.auth.forgotPassword(forgotEmail);
      setForgotSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setForgotLoading(false);
    }
  };

  if (isLoading || tenantsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 px-4 py-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/shop" className="inline-block mb-4">
            <h1 className="text-3xl font-bold text-gray-900 hover:text-green-700 transition-colors">
              🛒 Folies Boutique
            </h1>
          </Link>
          <p className="text-gray-600">
            {mode === 'login' ? 'Connexion à votre compte' : 'Créer votre compte'}
          </p>
        </div>

        {/* Login/Register Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Mode Switch Tabs */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => setMode('login')}
                className={`flex-1 py-2 rounded-md font-medium transition-all ${
                  mode === 'login'
                    ? 'bg-white text-green-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Connexion
              </button>
              <button
                type="button"
                onClick={() => setMode('register')}
                className={`flex-1 py-2 rounded-md font-medium transition-all ${
                  mode === 'register'
                    ? 'bg-white text-green-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Inscription
              </button>
            </div>

            {/* Sélecteur de boutique — masqué tant qu'il n'y en a qu'une.
                C'était la PREMIÈRE ligne du formulaire, avant même l'email :
                un menu obligatoire dont l'unique option lisait « Les Folies
                Maraîchères (Non spécifié) », qui parlait de livraisons alors
                que la boutique ne livre pas. La présélection automatique
                remplit toujours le champ, donc la validation passe. Le
                sélecteur réapparaîtra le jour où un second hub existera — il
                faudra alors en réécrire le libellé. */}
            {tenants.length > 1 && (
            <div>
              <label htmlFor="tenant" className="block text-sm font-medium text-gray-700 mb-1">
                Votre boutique locale *
                <span className="text-gray-600 text-xs ml-2">(pour les livraisons)</span>
              </label>
              <select
                id="tenant"
                value={formData.tenantId}
                onChange={(e) => handleFieldChange('tenantId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors bg-white"
                disabled={loading}
              >
                <option value="">-- Sélectionnez votre ville --</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.city})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-600 mt-1">
                💡 Les produits proviendront de cette boutique
              </p>
            </div>
            )}

            {/* Register-only fields */}
            {mode === 'register' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom *
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleFieldChange('firstName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Jean"
                      disabled={loading}
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleFieldChange('lastName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Dupont"
                      disabled={loading}
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone (optionnel)
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    placeholder="+32 123 45 67 89"
                    disabled={loading}
                    autoComplete="tel"
                  />
                </div>

                {/* Consentement marketing (opt-in, non pré-coché — RGPD) */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="w-5 h-5 mt-0.5 text-green-700 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-600">
                    J'accepte de recevoir des offres, actualites et promotions par email et SMS (facultatif)
                  </span>
                </label>
              </>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                placeholder="jean.dupont@email.com"
                disabled={loading}
                autoComplete="email"
                autoFocus={mode === 'login'}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe * {mode === 'register' && <span className="text-gray-600">(min. 8 caractères)</span>}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="••••••••"
                  disabled={loading}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                {/* ⚠️ `tabIndex={-1}` retiré : il rendait ce bouton INATTEIGNABLE au
                    clavier. Quelqu'un qui tape son mot de passe à l'oreille ne
                    pouvait ni le révéler ni le faire relire (audit du 15/09/2026).
                    Et un libellé fait d'un seul emoji n'apprend rien : un lecteur
                    d'écran annonçait « bouton » et rien d'autre. */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <span aria-hidden="true">{showPassword ? '👁️' : '👁️‍🗨️'}</span>
                </button>
              </div>
            </div>

            {/* Forgot Password (Login only) */}
            {mode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  className="text-sm text-green-700 hover:text-green-700 hover:underline"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setForgotEmail(formData.email);
                    setForgotSuccess(false);
                    setError(null);
                  }}
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700 text-center">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-medium text-lg hover:bg-green-700 focus:ring-4 focus:ring-green-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  {mode === 'login' ? 'Connexion...' : 'Inscription...'}
                </span>
              ) : (
                mode === 'login' ? 'Se connecter' : 'Créer mon compte'
              )}
            </button>
          </form>

          {/* Bascule connexion / création.
              Un LIEN au milieu d'une phrase se voyait trop peu : une cliente a
              passé une semaine à taper un mot de passe neuf sur le formulaire de
              CONNEXION — qui est le mode par défaut — et l'a compris comme
              « mon mot de passe est refusé » (signalé le 12/09/2026). Un bouton
              encadré, à la taille du doigt, dit qu'il y a deux chemins. */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-2">
              {mode === 'login' ? "Vous n'avez pas encore de compte ?" : 'Vous avez déjà un compte ?'}
            </p>
            <button
              type="button"
              onClick={switchMode}
              className="w-full min-h-[48px] px-4 py-3 border-2 border-green-600 text-green-700 rounded-lg font-semibold hover:bg-green-50 transition-colors"
            >
              {mode === 'login' ? 'Créer un compte' : 'Se connecter'}
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-600">ou</span>
            </div>
          </div>

          {/* Continue Shopping */}
          <Link
            to="/shop"
            className="block w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium text-center hover:bg-gray-200 transition-colors"
          >
            Continuer sans compte
          </Link>

          {/* Info */}
          {mode === 'register' && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-xs text-gray-600 text-center">
                En creant un compte, vous acceptez nos{' '}
                <Link to="/shop/cgv" className="text-green-700 hover:underline font-medium">CGV</Link>
                {' '}et notre{' '}
                <Link to="/shop/privacy" className="text-green-700 hover:underline font-medium">Politique de confidentialite</Link>.
                Vous pourrez suivre vos commandes, gerer votre portefeuille et profiter d'une experience personnalisee.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-400">
            <Link to="/shop/cgv" className="hover:underline">CGV</Link>
            {' · '}
            <Link to="/shop/privacy" className="hover:underline">Confidentialite</Link>
            {' · '}
            <Link to="/shop/mentions-legales" className="hover:underline">Mentions legales</Link>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            &copy; {new Date().getFullYear()} Les Folies Maraicheres SRL — T.V.A. 0715954426
          </p>
        </div>
      </div>

      {/* Modal Mot de passe oublié */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Mot de passe oublié</h2>

            {forgotSuccess ? (
              <div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-700 text-sm">
                    Si un compte existe avec l'adresse <strong>{forgotEmail}</strong>, un email
                    contenant un lien de réinitialisation vient d'être envoyé. Vérifiez également vos spams.
                  </p>
                </div>

                {/* LA SORTIE DU CUL-DE-SAC.
                    Par sécurité, une adresse inconnue reçoit exactement le même
                    message qu'une adresse connue, et aucun email ne part : sans
                    cela, n'importe qui découvrirait quelles adresses ont un compte
                    ici. Mais sans cette phrase, la personne attend un lien qui ne
                    partira jamais — c'est arrivé à une cliente qui a essayé avec
                    DEUX adresses, une semaine durant (12/09/2026). On garde
                    l'anonymat, on donne la porte. */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-900 text-sm mb-3">
                    Rien ne vous parvient d'ici quelques minutes ? C'est probablement
                    que vous n'avez pas encore de compte chez nous.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotSuccess(false);
                      setMode('register');
                      setFormData((prev) => ({ ...prev, email: forgotEmail }));
                    }}
                    className="w-full min-h-[48px] px-4 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                  >
                    Créer mon compte
                  </button>
                </div>
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Retour à la connexion
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <p className="text-gray-600 text-sm mb-4">
                  Entrez votre adresse email. Vous recevrez un lien pour réinitialiser votre mot de passe.
                </p>

                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setError(null); }}
                  placeholder="votre@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors mb-4"
                  autoFocus
                  disabled={forgotLoading}
                />

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setError(null); }}
                    className="flex-1 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={forgotLoading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={forgotLoading || !forgotEmail}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {forgotLoading ? 'Envoi...' : 'Envoyer le lien'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
